import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// Command set modelled on the CDVI ATRIUM A22K capabilities:
//   - Door: unlock (timed), lockdown / release, normal (return to schedule),
//     toggle, set unlock-time
//   - Reader / keypad: enable / disable / set-mode (card/pin/both/either)
//   - Zone (alarm input): bypass / unbypass / arm-partition / disarm-partition
//   - Card & cardholder lifecycle (these go through DB then sync to controller)
//   - Holiday and schedule edits
//   - Access level assignments
//   - Floor (elevator A22KEC) enable / disable
//   - Controller: reboot, sync, firmware-upgrade
// ─────────────────────────────────────────────────────────────────────────────

export const commandKinds = [
  // Door
  'door.unlock',
  'door.lockdown',
  'door.release_lockdown',
  'door.normal',
  'door.toggle',
  'door.set_unlock_time',
  // Reader
  'reader.set_mode',
  'reader.enable',
  'reader.disable',
  // Zone (input)
  'zone.bypass',
  'zone.unbypass',
  'partition.arm',
  'partition.disarm',
  // Card
  'card.activate',
  'card.deactivate',
  'card.add',
  'card.remove',
  // Cardholder
  'cardholder.upsert',
  'cardholder.delete',
  'cardholder.set_access_levels',
  // Schedule / holiday
  'schedule.upsert',
  'schedule.delete',
  'schedule.override',
  'holiday.upsert',
  'holiday.delete',
  // Access level
  'access_level.upsert',
  'access_level.delete',
  // Elevator
  'floor.enable',
  'floor.disable',
  // Controller
  'controller.reboot',
  'controller.sync',
  'controller.firmware_upgrade',
] as const;

export const CommandKind = z.enum(commandKinds);
export type CommandKind = z.infer<typeof CommandKind>;

export const CommandStatus = z.enum(['pending', 'sent', 'acked', 'failed', 'expired']);
export type CommandStatus = z.infer<typeof CommandStatus>;

const baseCommand = z.object({
  id: z.guid(),
  controllerId: z.guid(),
  issuedByAppUserId: z.string().nullable(),
  issuedAt: z.iso.datetime(),
});

// ── Door ────────────────────────────────────────────────────────────────────
export const DoorUnlockCommand = baseCommand.extend({
  kind: z.literal('door.unlock'),
  payload: z.object({
    doorId: z.guid(),
    // The vendor's door id (e.g. "0" for Porte 1). Stamped by the Worker at
    // enqueue time from doors.vendor_door_id, so the relay — which has no DB —
    // can address the door directly without a uuid→vendor lookup.
    vendorDoorId: z.string(),
    durationSeconds: z.number().int().min(1).max(300).default(5),
  }),
});

export const DoorLockdownCommand = baseCommand.extend({
  kind: z.literal('door.lockdown'),
  payload: z.object({ doorId: z.guid() }),
});

export const DoorReleaseLockdownCommand = baseCommand.extend({
  kind: z.literal('door.release_lockdown'),
  payload: z.object({ doorId: z.guid() }),
});

export const DoorNormalCommand = baseCommand.extend({
  kind: z.literal('door.normal'),
  payload: z.object({ doorId: z.guid() }),
});

export const DoorToggleCommand = baseCommand.extend({
  kind: z.literal('door.toggle'),
  payload: z.object({ doorId: z.guid() }),
});

export const DoorSetUnlockTimeCommand = baseCommand.extend({
  kind: z.literal('door.set_unlock_time'),
  payload: z.object({
    doorId: z.guid(),
    unlockTimeSeconds: z.number().int().min(1).max(60),
  }),
});

// ── Reader ──────────────────────────────────────────────────────────────────
export const ReaderSetModeCommand = baseCommand.extend({
  kind: z.literal('reader.set_mode'),
  payload: z.object({
    readerId: z.guid(),
    mode: z.enum(['card_only', 'pin_only', 'card_and_pin', 'card_or_pin', 'disabled']),
  }),
});

export const ReaderEnableCommand = baseCommand.extend({
  kind: z.enum(['reader.enable', 'reader.disable']),
  payload: z.object({ readerId: z.guid() }),
});

// ── Zone / partition ────────────────────────────────────────────────────────
export const ZoneBypassCommand = baseCommand.extend({
  kind: z.enum(['zone.bypass', 'zone.unbypass']),
  payload: z.object({ zoneId: z.guid() }),
});

export const PartitionArmCommand = baseCommand.extend({
  kind: z.enum(['partition.arm', 'partition.disarm']),
  payload: z.object({
    partitionId: z.guid(),
    mode: z.enum(['away', 'stay', 'instant']).default('away'),
  }),
});

// ── Card ────────────────────────────────────────────────────────────────────
export const CardActivateCommand = baseCommand.extend({
  kind: z.enum(['card.activate', 'card.deactivate']),
  payload: z.object({ cardId: z.guid() }),
});

export const CardAddCommand = baseCommand.extend({
  kind: z.literal('card.add'),
  payload: z.object({
    cardholderId: z.guid(),
    cardNumber: z.string(),
    facilityCode: z.string().optional(),
    format: z.enum(['wiegand_26', 'wiegand_30', 'wiegand_44', 'mifare_classic', 'desfire_ev2']),
  }),
});

export const CardRemoveCommand = baseCommand.extend({
  kind: z.literal('card.remove'),
  payload: z.object({ cardId: z.guid() }),
});

// ── Cardholder ──────────────────────────────────────────────────────────────
export const CardholderUpsertCommand = baseCommand.extend({
  kind: z.literal('cardholder.upsert'),
  payload: z.object({
    cardholderId: z.guid(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.email().nullable().optional(),
    activeFrom: z.iso.datetime().nullable().optional(),
    activeUntil: z.iso.datetime().nullable().optional(),
  }),
});

export const CardholderDeleteCommand = baseCommand.extend({
  kind: z.literal('cardholder.delete'),
  payload: z.object({ cardholderId: z.guid() }),
});

export const CardholderSetAccessLevelsCommand = baseCommand.extend({
  kind: z.literal('cardholder.set_access_levels'),
  payload: z.object({
    cardholderId: z.guid(),
    accessLevelIds: z.array(z.guid()),
  }),
});

// ── Schedule / holiday ──────────────────────────────────────────────────────
export const ScheduleUpsertCommand = baseCommand.extend({
  kind: z.literal('schedule.upsert'),
  payload: z.object({
    scheduleId: z.guid(),
    name: z.string(),
    rules: z.array(
      z.object({
        daysOfWeek: z.array(z.number().int().min(0).max(6)),
        startMinute: z.number().int().min(0).max(1440),
        endMinute: z.number().int().min(0).max(1440),
      }),
    ),
  }),
});

export const ScheduleDeleteCommand = baseCommand.extend({
  kind: z.literal('schedule.delete'),
  payload: z.object({ scheduleId: z.guid() }),
});

export const ScheduleOverrideCommand = baseCommand.extend({
  kind: z.literal('schedule.override'),
  payload: z.object({
    scheduleId: z.guid(),
    state: z.enum(['active', 'inactive']),
    durationMinutes: z.number().int().min(1).max(24 * 60),
  }),
});

export const HolidayUpsertCommand = baseCommand.extend({
  kind: z.literal('holiday.upsert'),
  payload: z.object({
    holidayId: z.guid(),
    name: z.string(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    repeatYearly: z.boolean().default(true),
  }),
});

export const HolidayDeleteCommand = baseCommand.extend({
  kind: z.literal('holiday.delete'),
  payload: z.object({ holidayId: z.guid() }),
});

// ── Access level ────────────────────────────────────────────────────────────
export const AccessLevelUpsertCommand = baseCommand.extend({
  kind: z.literal('access_level.upsert'),
  payload: z.object({
    accessLevelId: z.guid(),
    name: z.string(),
    scheduleId: z.guid().nullable(),
    doorIds: z.array(z.guid()),
  }),
});

export const AccessLevelDeleteCommand = baseCommand.extend({
  kind: z.literal('access_level.delete'),
  payload: z.object({ accessLevelId: z.guid() }),
});

// ── Elevator ────────────────────────────────────────────────────────────────
export const FloorEnableCommand = baseCommand.extend({
  kind: z.enum(['floor.enable', 'floor.disable']),
  payload: z.object({
    floorNumber: z.number().int().min(0).max(256),
  }),
});

// ── Controller ──────────────────────────────────────────────────────────────
export const ControllerRebootCommand = baseCommand.extend({
  kind: z.literal('controller.reboot'),
  payload: z.object({}).default({}),
});

export const ControllerSyncCommand = baseCommand.extend({
  kind: z.literal('controller.sync'),
  payload: z.object({}).default({}),
});

export const FirmwareUpgradeCommand = baseCommand.extend({
  kind: z.literal('controller.firmware_upgrade'),
  payload: z.object({
    firmwareUrl: z.url(),
    targetVersion: z.string(),
  }),
});

// ── Discriminated union ─────────────────────────────────────────────────────
export const RelayCommand = z.discriminatedUnion('kind', [
  DoorUnlockCommand,
  DoorLockdownCommand,
  DoorReleaseLockdownCommand,
  DoorNormalCommand,
  DoorToggleCommand,
  DoorSetUnlockTimeCommand,
  ReaderSetModeCommand,
  ReaderEnableCommand,
  ZoneBypassCommand,
  PartitionArmCommand,
  CardActivateCommand,
  CardAddCommand,
  CardRemoveCommand,
  CardholderUpsertCommand,
  CardholderDeleteCommand,
  CardholderSetAccessLevelsCommand,
  ScheduleUpsertCommand,
  ScheduleDeleteCommand,
  ScheduleOverrideCommand,
  HolidayUpsertCommand,
  HolidayDeleteCommand,
  AccessLevelUpsertCommand,
  AccessLevelDeleteCommand,
  FloorEnableCommand,
  ControllerRebootCommand,
  ControllerSyncCommand,
  FirmwareUpgradeCommand,
]);
export type RelayCommand = z.infer<typeof RelayCommand>;

export const CommandAck = z.object({
  commandId: z.guid(),
  status: z.enum(['acked', 'failed']),
  error: z.string().nullable().optional(),
  ackedAt: z.iso.datetime(),
});
export type CommandAck = z.infer<typeof CommandAck>;

export const RelayPollResponse = z.object({
  controllerId: z.guid(),
  commands: z.array(RelayCommand),
  serverTime: z.iso.datetime(),
});
export type RelayPollResponse = z.infer<typeof RelayPollResponse>;

export const commandLabels: Record<CommandKind, string> = {
  'door.unlock': 'Unlock door',
  'door.lockdown': 'Lockdown door',
  'door.release_lockdown': 'Release lockdown',
  'door.normal': 'Return door to schedule',
  'door.toggle': 'Toggle door',
  'door.set_unlock_time': 'Set unlock time',
  'reader.set_mode': 'Set reader mode',
  'reader.enable': 'Enable reader',
  'reader.disable': 'Disable reader',
  'zone.bypass': 'Bypass zone',
  'zone.unbypass': 'Un-bypass zone',
  'partition.arm': 'Arm partition',
  'partition.disarm': 'Disarm partition',
  'card.activate': 'Activate card',
  'card.deactivate': 'Deactivate card',
  'card.add': 'Add card',
  'card.remove': 'Remove card',
  'cardholder.upsert': 'Save cardholder',
  'cardholder.delete': 'Delete cardholder',
  'cardholder.set_access_levels': 'Update access levels',
  'schedule.upsert': 'Save schedule',
  'schedule.delete': 'Delete schedule',
  'schedule.override': 'Override schedule',
  'holiday.upsert': 'Save holiday',
  'holiday.delete': 'Delete holiday',
  'access_level.upsert': 'Save access level',
  'access_level.delete': 'Delete access level',
  'floor.enable': 'Enable floor',
  'floor.disable': 'Disable floor',
  'controller.reboot': 'Reboot controller',
  'controller.sync': 'Sync controller',
  'controller.firmware_upgrade': 'Upgrade firmware',
};
